// Centralized data for all tests
export const testingData = {
  'user-edit': {
    sectionId: 'user-edit',
    title: 'User Edit/Create Testing',
    subsections: [
      {
        title: 'User Creation Testing',
        tests: [
          {
            testId: 'user-create-1',
            description: 'Create new user with provider role and valid data',
            expectedResult: 'User created successfully, invitation sent, all required provider fields saved correctly'
          },
          {
            testId: 'user-create-2',
            description: 'Create new user with assistant role',
            expectedResult: 'User created successfully, provider-specific fields hidden and not required'
          },
          {
            testId: 'user-create-3',
            description: 'Test required field validation (missing firstName, lastName, email)',
            expectedResult: 'Form validation prevents submission, shows appropriate error messages'
          },
          {
            testId: 'user-create-4',
            description: 'Test provider role validation (missing providerTitle or NPI)',
            expectedResult: 'Form shows validation errors for missing provider-specific fields'
          },
          {
            testId: 'user-create-5',
            description: 'Test invalid email format validation',
            expectedResult: 'Email validation prevents submission with appropriate error message'
          },
          {
            testId: 'user-create-6',
            description: 'Test NPI number format validation (must be 10 digits)',
            expectedResult: 'NPI validation prevents submission if not exactly 10 digits'
          }
        ]
      },
      {
        title: 'User Editing Testing',
        tests: [
          {
            testId: 'user-edit-1',
            description: 'Edit existing user basic information (name, email)',
            expectedResult: 'Changes saved successfully, user data updated in system'
          },
          {
            testId: 'user-edit-2',
            description: 'Change user role from assistant to provider',
            expectedResult: 'Provider fields become visible and required, validation enforced'
          },
          {
            testId: 'user-edit-3',
            description: 'Change user role from provider to assistant',
            expectedResult: 'Provider fields hidden, validation removed, data cleared'
          },
          {
            testId: 'user-edit-4',
            description: 'Update user permissions (toggle various checkboxes)',
            expectedResult: 'Permission changes saved correctly, dependent permissions updated'
          },
          {
            testId: 'user-edit-5',
            description: 'Test permission dependencies (receiveClaimsEmails requires administerClaims)',
            expectedResult: 'receiveClaimsEmails disabled when administerClaims is false'
          }
        ]
      },
      {
        title: 'Permission Testing',
        tests: [
          {
            testId: 'user-perm-1',
            description: 'Test user creation with ACCOUNT_USER_CREATE permission',
            expectedResult: 'Create user form accessible and functional'
          },
          {
            testId: 'user-perm-2',
            description: 'Test user creation without ACCOUNT_USER_CREATE permission',
            expectedResult: 'Access denied or create button disabled'
          },
          {
            testId: 'user-perm-3',
            description: 'Test user editing with ACCOUNT_USER_PATCH permission',
            expectedResult: 'Edit user form accessible and functional'
          },
          {
            testId: 'user-perm-4',
            description: 'Test user deletion with ACCOUNT_USER_DELETE permission',
            expectedResult: 'Delete button available and functional'
          },
          {
            testId: 'user-perm-5',
            description: 'Test user deletion without permission or business rule violation',
            expectedResult: 'Delete button disabled or error message shown'
          }
        ]
      },
      {
        title: 'Distribution Sponsor Testing',
        tests: [
          {
            testId: 'user-ds-1',
            description: 'Test DS account user management with ACCOUNTS_GET permission',
            expectedResult: 'allowedAccounts field visible, multi-account management available'
          },
          {
            testId: 'user-ds-2',
            description: 'Test DS user without ACCOUNTS_GET permission',
            expectedResult: 'allowedAccounts field hidden, standard account management only'
          },
          {
            testId: 'user-ds-3',
            description: 'Test setting specific account access restrictions',
            expectedResult: 'User limited to selected accounts only, empty array means all access'
          },
          {
            testId: 'user-ds-4',
            description: 'Test DS route structure (/accounts/{accountUuid}/users)',
            expectedResult: 'Proper routing for DS account management, breadcrumbs show account context'
          },
          {
            testId: 'user-ds-5',
            description: 'Test email permission visibility for DS accounts',
            expectedResult: 'receiveClaimsEmails and receiveSurveysEmails hidden for DS account users'
          }
        ]
      },
      {
        title: 'User List Testing',
        tests: [
          {
            testId: 'user-list-1',
            description: 'Test user list display with ACCOUNT_USERS_GET permission',
            expectedResult: 'User list loads with all users, proper pagination and sorting'
          },
          {
            testId: 'user-list-2',
            description: 'Test user list search and filtering functionality',
            expectedResult: 'Search works correctly, filters apply properly'
          },
          {
            testId: 'user-list-3',
            description: 'Test resend invitation functionality',
            expectedResult: 'Invitation resent successfully, appropriate success message shown'
          },
          {
            testId: 'user-list-4',
            description: 'Test invitation limit enforcement',
            expectedResult: 'Error message when too many invitations sent'
          },
          {
            testId: 'user-list-5',
            description: 'Test user status indicators (invited, active, etc.)',
            expectedResult: 'User status displayed correctly with appropriate visual indicators'
          },
          {
            testId: 'user-list-6',
            description: 'Test standard account table configuration (4 columns)',
            expectedResult: 'Table shows First Name, Last Name, Role, Email columns only'
          },
          {
            testId: 'user-list-7',
            description: 'Test DS account table configuration (8 columns)',
            expectedResult: 'Table shows additional Custom Tags, Account, Custom ID, Access Granted columns'
          },
          {
            testId: 'user-list-8',
            description: 'Test row click navigation to user edit form',
            expectedResult: 'Clicking user row navigates to correct edit route (standard or DS)'
          },
          {
            testId: 'user-list-9',
            description: 'Test Add User button functionality',
            expectedResult: 'Add User button navigates to create form with proper account context'
          },
          {
            testId: 'user-list-10',
            description: 'Test user deletion confirmation dialog',
            expectedResult: 'Delete action shows confirmation dialog with user name, removes on confirm'
          },
          {
            testId: 'user-list-11',
            description: 'Test user deletion business rule enforcement',
            expectedResult: 'Error message when user cannot be removed due to business constraints'
          },
          {
            testId: 'user-list-12',
            description: 'Test DS cross-account user management',
            expectedResult: 'DS accounts can view and manage users from child accounts'
          },
          {
            testId: 'user-list-13',
            description: 'Test custom tags display as colored bubbles (DS only)',
            expectedResult: 'Account custom tags appear as colored chip elements'
          },
          {
            testId: 'user-list-14',
            description: 'Test table sorting by firstName (default ascending)',
            expectedResult: 'Table loads with users sorted by first name A-Z'
          },
          {
            testId: 'user-list-15',
            description: 'Test virtual scrolling with large user datasets',
            expectedResult: 'Table handles large user lists efficiently with smooth scrolling'
          }
        ]
      },
      {
        title: 'API Integration Testing',
        tests: [
          {
            testId: 'user-api-1',
            description: 'Test successful user creation API call (PUT /accounts/{uuid}/users)',
            expectedResult: '201 Created response with complete user object including UUID'
          },
          {
            testId: 'user-api-2',
            description: 'Test user creation validation errors (422 response)',
            expectedResult: 'Proper error handling, validation messages displayed to user'
          },
          {
            testId: 'user-api-3',
            description: 'Test user update API call (PATCH with JSON Patch format)',
            expectedResult: '200 OK response with updated user object'
          },
          {
            testId: 'user-api-4',
            description: 'Test user deletion API call (DELETE)',
            expectedResult: '204 No Content response, user removed from system'
          },
          {
            testId: 'user-api-5',
            description: 'Test business rule violation on deletion (500 error)',
            expectedResult: 'Proper error handling, user informed they cannot delete this user'
          }
        ]
      }
    ]
  },

  'user-list': {
    sectionId: 'user-list',
    title: 'User List Testing',
    subsections: [
      {
        title: 'Permission Testing',
        tests: [
          {
            testId: 'user-list-perm-1',
            description: 'Test ACCOUNT_USERS_GET permission requirement for list access',
            expectedResult: 'User list loads when permission exists, access denied message when missing'
          },
          {
            testId: 'user-list-perm-2',
            description: 'Test ACCOUNT_USER_CREATE permission for Add User button',
            expectedResult: 'Add User button visible and functional with permission, hidden without'
          },
          {
            testId: 'user-list-perm-3',
            description: 'Test ACCOUNTS_GET permission for DS account features',
            expectedResult: 'DS accounts see additional columns and cross-account functionality'
          },
          {
            testId: 'user-list-perm-4',
            description: 'Test DS vs regular account functionality differences',
            expectedResult: 'DS accounts use usersDSTable config, regular accounts use usersTable config'
          }
        ]
      },
      {
        title: 'Table Display Testing',
        tests: [
          {
            testId: 'user-list-display-1',
            description: 'Test standard account table columns (4 columns)',
            expectedResult: 'Table shows First Name, Last Name, Role, Email columns only'
          },
          {
            testId: 'user-list-display-2',
            description: 'Test DS account table columns (8 columns)',
            expectedResult: 'Table shows additional Custom Tags, Account, Custom ID, Access Granted columns'
          },
          {
            testId: 'user-list-display-3',
            description: 'Test default sorting by firstName ascending',
            expectedResult: 'Table loads with users sorted alphabetically by first name A-Z'
          },
          {
            testId: 'user-list-display-4',
            description: 'Test custom tags display as colored bubbles (DS only)',
            expectedResult: 'Account custom tags appear as colored chip elements with appropriate colors'
          },
          {
            testId: 'user-list-display-5',
            description: 'Test role display formatting',
            expectedResult: 'User roles display as provider/assistant with proper formatting'
          }
        ]
      },
      {
        title: 'Filtering and Search Testing',
        tests: [
          {
            testId: 'user-list-filter-1',
            description: 'Test text filters (firstName, lastName, email)',
            expectedResult: 'Text filters work with partial matches, results update in real-time'
          },
          {
            testId: 'user-list-filter-2',
            description: 'Test DS-only select filters (customTags, businessName)',
            expectedResult: 'DS accounts see additional dropdown filters, regular accounts do not'
          },
          {
            testId: 'user-list-filter-3',
            description: 'Test access granted filter (isAllowed)',
            expectedResult: 'Checkbox filter works correctly with Allowed/Not Allowed/All options'
          },
          {
            testId: 'user-list-filter-4',
            description: 'Test filter persistence across browser sessions',
            expectedResult: 'Applied filters remain active when user returns to page'
          }
        ]
      },
      {
        title: 'Row Actions Testing',
        tests: [
          {
            testId: 'user-list-actions-1',
            description: 'Test Edit User action availability and navigation',
            expectedResult: 'Edit action always available, navigates to correct route (standard or DS)'
          },
          {
            testId: 'user-list-actions-2',
            description: 'Test Resend Invitation conditional visibility',
            expectedResult: 'Resend action only shows when canResendInvitation is true'
          },
          {
            testId: 'user-list-actions-3',
            description: 'Test Resend Invitation functionality',
            expectedResult: 'Invitation resent successfully, success toast notification shown'
          },
          {
            testId: 'user-list-actions-4',
            description: 'Test Resend Invitation rate limiting',
            expectedResult: 'Error message when too many invitations sent (error code 1)'
          },
          {
            testId: 'user-list-actions-5',
            description: 'Test Remove User confirmation dialog',
            expectedResult: 'Delete shows confirmation dialog with user name, removes on confirmation'
          },
          {
            testId: 'user-list-actions-6',
            description: 'Test Remove User business rule enforcement',
            expectedResult: 'Error message when user cannot be removed due to business constraints'
          }
        ]
      },
      {
        title: 'Table Features Testing',
        tests: [
          {
            testId: 'user-list-table-1',
            description: 'Test virtual scrolling with large user datasets',
            expectedResult: 'Table handles large user lists efficiently with smooth scrolling performance'
          },
          {
            testId: 'user-list-table-2',
            description: 'Test column sorting functionality',
            expectedResult: 'All columns sort correctly when clicked, sort indicators display properly'
          },
          {
            testId: 'user-list-table-3',
            description: 'Test row click navigation to user edit',
            expectedResult: 'Clicking user row navigates to appropriate edit page with correct parameters'
          },
          {
            testId: 'user-list-table-4',
            description: 'Test Add User button functionality',
            expectedResult: 'Add User button navigates to create form with proper account context'
          },
          {
            testId: 'user-list-table-5',
            description: 'Test pagination and infinite loading',
            expectedResult: 'More users load automatically as user scrolls, loading indicators show'
          }
        ]
      },
      {
        title: 'DS Account Specific Testing',
        tests: [
          {
            testId: 'user-list-ds-1',
            description: 'Test cross-account user visibility for DS accounts',
            expectedResult: 'DS accounts can view users from multiple child accounts in table'
          },
          {
            testId: 'user-list-ds-2',
            description: 'Test DS user edit navigation (cross-account route)',
            expectedResult: 'DS accounts navigate to DS-specific edit route with accountUuid parameter'
          },
          {
            testId: 'user-list-ds-3',
            description: 'Test DS user creation navigation',
            expectedResult: 'DS accounts navigate to DS-specific create route for child accounts'
          },
          {
            testId: 'user-list-ds-4',
            description: 'Test account context preservation',
            expectedResult: 'Account context maintained correctly throughout DS user management flow'
          }
        ]
      }
    ]
  },

  dashboard: {
    sectionId: 'dashboard',
    title: 'Dashboard Testing',
    subsections: [
      {
        title: 'Permission Testing',
        tests: [
          {
            testId: 'dashboard-perm-1',
            description: 'Test dashboard access with DASHBOARD_GET permission',
            expectedResult: 'Dashboard loads successfully with all widgets visible'
          },
          {
            testId: 'dashboard-perm-2',
            description: 'Test dashboard denial without proper permissions',
            expectedResult: 'Access denied message or redirect to login/authorized page'
          },
          {
            testId: 'dashboard-perm-3',
            description: 'Test feature availability based on account type (DS vs Regular)',
            expectedResult: 'DS accounts see additional cross-account features, regular accounts see standard dashboard'
          }
        ]
      },
      {
        title: 'Data Display Testing',
        tests: [
          {
            testId: 'dashboard-data-1',
            description: 'Test all three information blocks load correctly (Patients, Female %, Average Age)',
            expectedResult: 'All three metric cards display with numbers or \'-\' for empty data, grow animation plays'
          },
          {
            testId: 'dashboard-data-2',
            description: 'Verify all 10 chart categories display properly',
            expectedResult: '10 charts render correctly with data or empty state indicators'
          },
          {
            testId: 'dashboard-data-3',
            description: 'Test assessment alerts table population and interactions',
            expectedResult: 'Table populates with assessment data, row clicks navigate to reports'
          },
          {
            testId: 'dashboard-data-4',
            description: 'Test empty data scenarios (show \'-\' for missing metrics)',
            expectedResult: 'Missing data displays as \'-\' symbol in metric cards and empty states in charts'
          },
          {
            testId: 'dashboard-data-5',
            description: 'Test assessment status tracking (Invited, In Progress, Scheduled, No Show)',
            expectedResult: 'Assessment statuses display correctly based on current state and update in real-time'
          }
        ]
      },
      {
        title: 'Interaction Testing',
        tests: [
          {
            testId: 'dashboard-interaction-1',
            description: 'Test Add Patient modal opening and functionality',
            expectedResult: 'Modal opens when button clicked (if PATIENT_CREATE permission exists)'
          },
          {
            testId: 'dashboard-interaction-2',
            description: 'Test chart type switching (Pie/Bar/Line toggle functionality)',
            expectedResult: 'Charts change type when toggled, preference saved to localStorage'
          },
          {
            testId: 'dashboard-interaction-3',
            description: 'Test assessment row click navigation to reports',
            expectedResult: 'Clicking assessment table row navigates to assessment report page with correct parameters'
          },
          {
            testId: 'dashboard-interaction-4',
            description: 'Test patient Info Pane opening from data cells',
            expectedResult: 'Info pane slides out from right when patient data cells are clicked'
          }
        ]
      },
      {
        title: 'Responsive Testing',
        tests: [
          {
            testId: 'dashboard-responsive-1',
            description: 'Test dashboard adaptation on mobile devices',
            expectedResult: 'Dashboard layout adapts with responsive grid, charts resize properly'
          },
          {
            testId: 'dashboard-responsive-2',
            description: 'Test collapsible navigation functionality',
            expectedResult: 'Sidebar collapses/expands on mobile, state persists across sessions'
          },
          {
            testId: 'dashboard-responsive-3',
            description: 'Test chart resizing with window changes',
            expectedResult: 'Charts automatically resize and redraw when browser window size changes'
          }
        ]
      },
      {
        title: 'Error Scenario Testing',
        tests: [
          {
            testId: 'dashboard-error-1',
            description: 'Test error state displays and recovery',
            expectedResult: 'Error UI shows when data fails to load, with retry options where applicable'
          },
          {
            testId: 'dashboard-error-2',
            description: 'Test graceful handling of no data scenarios',
            expectedResult: 'Empty states display with appropriate messaging, no crashes or errors'
          },
          {
            testId: 'dashboard-error-3',
            description: 'Test appropriate redirects and error messages for permission denials',
            expectedResult: 'User redirected to authorized page with clear permission error message'
          }
        ]
      },
      {
        title: 'Session Management Testing',
        tests: [
          {
            testId: 'dashboard-session-1',
            description: 'Test automatic logout after 30 minutes of inactivity',
            expectedResult: 'User is automatically logged out and redirected to login page after 30 minutes of inactivity'
          },
          {
            testId: 'dashboard-session-2',
            description: 'Test session timeout warning display',
            expectedResult: 'Warning appears before automatic logout with countdown timer and option to extend session'
          },
          {
            testId: 'dashboard-session-3',
            description: 'Test session extension functionality',
            expectedResult: 'User activity resets the inactivity timer and prevents automatic logout'
          }
        ]
      }
    ]
  },

  'patient-list': {
    sectionId: 'patient-list',
    title: 'Patient List Testing',
    subsections: [
      {
        title: 'Permission Testing',
        tests: [
          {
            testId: 'patient-list-perm-1',
            description: 'Verify PATIENTS_GET permission requirement for table access',
            expectedResult: 'Patient list loads when permission exists, access denied message when missing'
          },
          {
            testId: 'patient-list-perm-2',
            description: 'Test button/action visibility based on permissions',
            expectedResult: 'Action buttons (Create Order, Edit, Delete) only visible with appropriate permissions'
          },
          {
            testId: 'patient-list-perm-3',
            description: 'Test DS vs regular account functionality differences',
            expectedResult: 'DS accounts see Account column and cross-account patients, regular accounts see single-account view'
          },
          {
            testId: 'patient-list-perm-4',
            description: 'Test ACCOUNTS_GET permission for DS account columns',
            expectedResult: 'Account column visible only for DS accounts with ACCOUNTS_GET permission'
          }
        ]
      },
      {
        title: 'Data Display Testing',
        tests: [
          {
            testId: 'patient-list-data-1',
            description: 'Test all 8 table columns display correctly',
            expectedResult: 'All columns (First Name, Last Name, MRN, Custom ID, Account, DOB, Custom Tags, Account Tags) render properly'
          },
          {
            testId: 'patient-list-data-2',
            description: 'Test custom tags display as colored bubbles',
            expectedResult: 'Custom tags appear as colored chip/bubble elements with appropriate colors'
          },
          {
            testId: 'patient-list-data-3',
            description: 'Test date formatting in localized format',
            expectedResult: 'Dates display in MM/DD/YYYY format (or user\'s locale format)'
          },
          {
            testId: 'patient-list-data-4',
            description: 'Test empty value display (\'-\' or \'N/A\')',
            expectedResult: 'Empty cells show \'-\' or \'N/A\' instead of blank spaces'
          }
        ]
      },
      {
        title: 'Filtering and Search Testing',
        tests: [
          {
            testId: 'patient-list-filter-1',
            description: 'Test text filters (First Name, Last Name, MRN, Custom ID)',
            expectedResult: 'Text filters work with partial matches, results update in real-time as user types'
          },
          {
            testId: 'patient-list-filter-2',
            description: 'Test select filters (Account Custom Tags, Custom Tags, Account)',
            expectedResult: 'Dropdown filters populate with available options, multiple selections work correctly'
          },
          {
            testId: 'patient-list-filter-3',
            description: 'Test date range filter for Date of Birth',
            expectedResult: 'Date picker allows range selection, table filters to show patients within selected DOB range'
          },
          {
            testId: 'patient-list-filter-4',
            description: 'Test filter persistence across browser sessions',
            expectedResult: 'Applied filters remain active when user returns to page in same browser session'
          }
        ]
      },
      {
        title: 'Row Actions Testing',
        tests: [
          {
            testId: 'patient-list-actions-1',
            description: 'Test Create Order action with ORDER_CREATE permission',
            expectedResult: 'Create Order button appears and functions correctly when permission exists'
          },
          {
            testId: 'patient-list-actions-2',
            description: 'Test Create Assessment action with ORDER_SURVEY_CREATE permission',
            expectedResult: 'Create Assessment button appears and opens assessment creation flow when permission exists'
          },
          {
            testId: 'patient-list-actions-3',
            description: 'Test Edit Patient action opening modal',
            expectedResult: 'Edit action opens patient edit modal with pre-populated data'
          },
          {
            testId: 'patient-list-actions-4',
            description: 'Test Remove Patient action with confirmation dialog',
            expectedResult: 'Delete action shows confirmation dialog, removes patient on confirmation'
          }
        ]
      },
      {
        title: 'Table Features Testing',
        tests: [
          {
            testId: 'patient-list-table-1',
            description: 'Test virtual scrolling with large datasets',
            expectedResult: 'Table handles large patient lists efficiently with virtual scrolling, smooth performance'
          },
          {
            testId: 'patient-list-table-2',
            description: 'Test column sorting (default: lastName ascending)',
            expectedResult: 'Columns sort correctly when clicked, default sort by lastName ascending loads initially'
          },
          {
            testId: 'patient-list-table-3',
            description: 'Test column resizing and reordering',
            expectedResult: 'User can drag column borders to resize and drag headers to reorder columns'
          },
          {
            testId: 'patient-list-table-4',
            description: 'Test column hiding/showing (except Account column)',
            expectedResult: 'Column visibility menu allows hiding/showing columns, Account column always visible for DS accounts'
          },
          {
            testId: 'patient-list-table-5',
            description: 'Test InfoPane integration from clickable cells',
            expectedResult: 'Clicking patient name/MRN cells opens InfoPane with detailed patient information'
          }
        ]
      }
    ]
  },

  'patient-edit': {
    sectionId: 'patient-edit',
    title: 'Patient Edit/Create Testing',
    subsections: [
      {
        title: 'Field Validation Testing',
        tests: [
          {
            testId: 'patient-edit-validation-1',
            description: 'Test required field validation (mrnNumber, firstName, lastName, dateOfBirth, genderAtBirth)',
            expectedResult: 'Error messages appear for empty required fields, form submission prevented'
          },
          {
            testId: 'patient-edit-validation-2',
            description: 'Test MRN uniqueness validation within account',
            expectedResult: 'Error message shows when MRN already exists in current account'
          },
          {
            testId: 'patient-edit-validation-3',
            description: 'Test email format validation (optional field)',
            expectedResult: 'Invalid email formats show validation error, valid emails accepted'
          },
          {
            testId: 'patient-edit-validation-4',
            description: 'Test phone number US format validation',
            expectedResult: 'Phone numbers validate to US format (XXX) XXX-XXXX, auto-formatting applied'
          },
          {
            testId: 'patient-edit-validation-5',
            description: 'Test date of birth past date validation',
            expectedResult: 'Future dates rejected with error message, past dates accepted'
          },
          {
            testId: 'patient-edit-validation-6',
            description: 'Test gender enum validation (male/female)',
            expectedResult: 'Only male/female options available, selection required for genderAtBirth'
          },
          {
            testId: 'patient-edit-validation-7',
            description: 'Test drug allergies max length (1000 characters)',
            expectedResult: 'Character count shows, prevents input beyond 1000 characters'
          },
          {
            testId: 'patient-edit-validation-8',
            description: 'Test drug allergies required when hasDrugAllergies is true',
            expectedResult: 'Drug allergies field becomes required when \'Has Drug Allergies\' is checked'
          }
        ]
      },
      {
        title: 'Form Behavior Testing',
        tests: [
          {
            testId: 'patient-edit-form-1',
            description: 'Test form pre-population in edit mode',
            expectedResult: 'All existing patient data loads correctly in form fields'
          },
          {
            testId: 'patient-edit-form-2',
            description: 'Test preferredPhone field read-only behavior',
            expectedResult: 'PreferredPhone field displays as read-only with tooltip explanation'
          },
          {
            testId: 'patient-edit-form-3',
            description: 'Test form reset functionality',
            expectedResult: 'Reset button clears form back to original state or empty for new patients'
          },
          {
            testId: 'patient-edit-form-4',
            description: 'Test field auto-formatting (email lowercase, phone digits)',
            expectedResult: 'Email converts to lowercase, phone numbers auto-format to (XXX) XXX-XXXX'
          },
          {
            testId: 'patient-edit-form-5',
            description: 'Test drug allergies conditional requirement',
            expectedResult: 'When \'Has Drug Allergies\' is Yes, drug allergies text field becomes required and validates'
          },
          {
            testId: 'patient-edit-form-6',
            description: 'Test drug allergies Unknown state',
            expectedResult: 'When drug allergies is \'Unknown\', order creation shows warning message to update patient record'
          }
        ]
      },
      {
        title: 'API Integration Testing',
        tests: [
          {
            testId: 'patient-edit-api-1',
            description: 'Test successful patient creation (PUT endpoint)',
            expectedResult: 'Success message shows, patient created with generated UUID, form stays in edit mode'
          },
          {
            testId: 'patient-edit-api-2',
            description: 'Test successful patient update (PATCH endpoint)',
            expectedResult: 'Success message shows, changes saved, timestamps updated'
          },
          {
            testId: 'patient-edit-api-3',
            description: 'Test validation error handling (400 responses)',
            expectedResult: 'Specific field errors displayed from backend validation, form highlights problem fields'
          },
          {
            testId: 'patient-edit-api-4',
            description: 'Test conflict error handling (409 - MRN exists)',
            expectedResult: 'Clear error message about MRN conflict, MRN field highlighted'
          },
          {
            testId: 'patient-edit-api-5',
            description: 'Test validate-only query parameter',
            expectedResult: 'Validation runs without saving when validate-only parameter used'
          }
        ]
      },
      {
        title: 'Permission Testing',
        tests: [
          {
            testId: 'patient-edit-perm-1',
            description: 'Test PATIENT_CREATE permission for creation',
            expectedResult: 'Create patient form accessible and functional with permission, blocked without'
          },
          {
            testId: 'patient-edit-perm-2',
            description: 'Test PATIENT_PATCH permission for editing',
            expectedResult: 'Edit patient form accessible and save enabled with permission, read-only without'
          },
          {
            testId: 'patient-edit-perm-3',
            description: 'Test PATIENT_GET permission for viewing',
            expectedResult: 'Patient data loads and displays with permission, access denied without'
          },
          {
            testId: 'patient-edit-perm-4',
            description: 'Test PATIENT_DELETE permission for deletion',
            expectedResult: 'Delete button visible and functional with permission, hidden without'
          }
        ]
      },
      {
        title: 'Email Integration Testing',
        tests: [
          {
            testId: 'patient-edit-email-1',
            description: 'Test Email Patient functionality from patient profile',
            expectedResult: '\'Email Patient\' button opens default email client with patient\'s email pre-filled in To field'
          },
          {
            testId: 'patient-edit-email-2',
            description: 'Test Email Patient with missing email address',
            expectedResult: 'Appropriate error message when patient has no email address configured'
          }
        ]
      },
      {
        title: 'Demographics Modification Testing',
        tests: [
          {
            testId: 'patient-edit-demo-1',
            description: 'Test Modify Demographics from claim page (DS accounts)',
            expectedResult: '\'Modify Demographics\' button opens patient edit form, changes save successfully'
          },
          {
            testId: 'patient-edit-demo-2',
            description: 'Test Modify Demographics from claim page (regular accounts)',
            expectedResult: '\'Modify Demographics\' button works for regular accounts with appropriate permissions'
          }
        ]
      }
    ]
  },

  'claims-list': {
    sectionId: 'claims-list',
    title: 'Claims List Testing',
    subsections: [
      {
        title: 'Permission Testing',
        tests: [
          {
            testId: 'claims-list-perm-1',
            description: 'Verify CLAIMS_GET permission requirement for table access',
            expectedResult: 'Claims list loads when permission exists, access denied message when missing'
          },
          {
            testId: 'claims-list-perm-2',
            description: 'Test SURVEYS_GET permission for PDF download functionality',
            expectedResult: 'Download Program PDF available only with SURVEYS_GET permission'
          },
          {
            testId: 'claims-list-perm-3',
            description: 'Test ACCOUNTS_GET permission for account columns visibility',
            expectedResult: 'Account-related columns (Custom Tags, Business Name, Custom ID) visible only with ACCOUNTS_GET'
          },
          {
            testId: 'claims-list-perm-4',
            description: 'Test DS vs regular account functionality differences',
            expectedResult: 'DS accounts see enhanced features, cross-account access, and additional filter options'
          }
        ]
      },
      {
        title: 'Table Display Testing',
        tests: [
          {
            testId: 'claims-list-display-1',
            description: 'Test all table columns display correctly (26+ columns)',
            expectedResult: 'All columns render with proper headers, formatting, and data display'
          },
          {
            testId: 'claims-list-display-2',
            description: 'Test default sorting by Claim Generated Date (descending)',
            expectedResult: 'Table loads with newest claims first, sort indicator shows on Claim Generated Date column'
          },
          {
            testId: 'claims-list-display-3',
            description: 'Test custom tags display as colored bubbles',
            expectedResult: 'Account custom tags appear as colored chip elements with appropriate colors'
          },
          {
            testId: 'claims-list-display-4',
            description: 'Test status columns color-coding and formatting',
            expectedResult: 'Billing Status and Claim Status display with appropriate colors and status indicators'
          },
          {
            testId: 'claims-list-display-5',
            description: 'Test financial columns formatting (currency display)',
            expectedResult: 'Total Charge, Total Paid, Total PR amounts display in proper currency format'
          }
        ]
      },
      {
        title: 'Filtering and Search Testing',
        tests: [
          {
            testId: 'claims-list-filter-1',
            description: 'Test text filters (Patient names, Account name, Custom IDs)',
            expectedResult: 'Text filters work with partial matches, results update in real-time as user types'
          },
          {
            testId: 'claims-list-filter-2',
            description: 'Test select filters (Billing Status, Claim Status, Entity Type)',
            expectedResult: 'Dropdown filters populate with available options, multiple selections work correctly'
          },
          {
            testId: 'claims-list-filter-3',
            description: 'Test date range filters (Order Date, Appointment Date, Document Date)',
            expectedResult: 'Date pickers allow range selection, table filters to show claims within selected date ranges'
          },
          {
            testId: 'claims-list-filter-4',
            description: 'Test Yes/No filters (Patient Seen, Document Uploaded)',
            expectedResult: 'Boolean filters work correctly with Yes/No/All options'
          },
          {
            testId: 'claims-list-filter-5',
            description: 'Test filter persistence across browser sessions',
            expectedResult: 'Applied filters remain active when user returns to page in same browser session'
          },
          {
            testId: 'claims-list-filter-6',
            description: 'Test DS vs Regular account filter option differences',
            expectedResult: 'DS accounts see additional billing status options (Billed, ICD Issue, Insurance Issue)'
          }
        ]
      },
      {
        title: 'Header Actions Testing',
        tests: [
          {
            testId: 'claims-list-header-1',
            description: 'Test Metrics Button functionality',
            expectedResult: 'Metrics button opens info pane with claims aggregations, counts, and financial summaries'
          },
          {
            testId: 'claims-list-header-2',
            description: 'Test CSV Export functionality',
            expectedResult: 'CSV export downloads file with format \'Claims [Date].csv\' containing current filtered data'
          },
          {
            testId: 'claims-list-header-3',
            description: 'Test Refresh button functionality',
            expectedResult: 'Refresh button reloads data while preserving current filters and sort settings'
          },
          {
            testId: 'claims-list-header-4',
            description: 'Test CSV export respects current filters and sorting',
            expectedResult: 'Exported CSV contains only filtered data in the same sort order as displayed'
          }
        ]
      },
      {
        title: 'Row Actions Testing',
        tests: [
          {
            testId: 'claims-list-row-1',
            description: 'Test Download Program PDF action availability',
            expectedResult: 'Download Program PDF appears only for Program entity types and users with SURVEYS_GET permission'
          },
          {
            testId: 'claims-list-row-2',
            description: 'Test Download Program PDF functionality',
            expectedResult: 'PDF downloads with correct filename format: \'Program: [Name] - [Patient] [Timestamp].pdf\''
          },
          {
            testId: 'claims-list-row-3',
            description: 'Test Download Program PDF error handling',
            expectedResult: '422 errors show validation messages, other errors show generic error message'
          },
          {
            testId: 'claims-list-row-4',
            description: 'Test row click navigation to claim edit',
            expectedResult: 'Clicking claim row navigates to appropriate edit page (DS or regular route)'
          },
          {
            testId: 'claims-list-row-5',
            description: 'Test loading states during PDF download',
            expectedResult: 'Download button shows loading indicator during PDF generation'
          }
        ]
      },
      {
        title: 'Table Features Testing',
        tests: [
          {
            testId: 'claims-list-table-1',
            description: 'Test virtual scrolling with large datasets',
            expectedResult: 'Table handles large claim lists efficiently with virtual scrolling, smooth performance'
          },
          {
            testId: 'claims-list-table-2',
            description: 'Test column sorting functionality',
            expectedResult: 'All sortable columns sort correctly when clicked, sort indicators display properly'
          },
          {
            testId: 'claims-list-table-3',
            description: 'Test multi-column sorting (Shift + Click)',
            expectedResult: 'Secondary sort works with Shift+Click, multiple sort indicators show correctly'
          },
          {
            testId: 'claims-list-table-4',
            description: 'Test column resizing and reordering',
            expectedResult: 'User can drag column borders to resize and drag headers to reorder columns'
          },
          {
            testId: 'claims-list-table-5',
            description: 'Test column hiding/showing functionality',
            expectedResult: 'Column visibility menu allows hiding/showing columns, settings persist across sessions'
          },
          {
            testId: 'claims-list-table-6',
            description: 'Test infinite loading/pagination',
            expectedResult: 'More rows load automatically as user scrolls, loading indicators show during fetch'
          }
        ]
      },
      {
        title: 'DS Account Specific Testing',
        tests: [
          {
            testId: 'claims-list-ds-1',
            description: 'Test cross-account claims visibility for DS accounts',
            expectedResult: 'DS accounts can view claims from all child accounts in the table'
          },
          {
            testId: 'claims-list-ds-2',
            description: 'Test DS-specific billing status filter options',
            expectedResult: 'DS accounts see additional options: Billed, ICD Issue, Insurance Issue in filters'
          },
          {
            testId: 'claims-list-ds-3',
            description: 'Test DS claim edit navigation (cross-account route)',
            expectedResult: 'DS accounts navigate to DS-specific edit route with accountUuid parameter'
          },
          {
            testId: 'claims-list-ds-4',
            description: 'Test DS table configuration differences',
            expectedResult: 'DS accounts use claimsDSTable configuration with enhanced features'
          }
        ]
      },
      {
        title: 'Error Handling Testing',
        tests: [
          {
            testId: 'claims-list-error-1',
            description: 'Test 403 Forbidden error handling',
            expectedResult: 'Insufficient permissions show appropriate access denied message or redirect'
          },
          {
            testId: 'claims-list-error-2',
            description: 'Test data loading error scenarios',
            expectedResult: 'Network errors show appropriate error messages with retry options'
          },
          {
            testId: 'claims-list-error-3',
            description: 'Test filter validation error handling',
            expectedResult: 'Invalid filter parameters show validation errors without breaking the table'
          },
          {
            testId: 'claims-list-error-4',
            description: 'Test empty data state display',
            expectedResult: 'Empty claims list shows appropriate \'No claims found\' message with helpful text'
          }
        ]
      }
    ]
  },

  'claims-edit': {
    sectionId: 'claims-edit',
    title: 'Claims Edit/Create Testing',
    subsections: [
      {
        title: 'Basic Field Validation',
        tests: [
          {
            testId: 'claims-field-1',
            description: 'Test Billing Status is required',
            expectedResult: 'Error message "Billing Status is required" when field is empty'
          },
          {
            testId: 'claims-field-2',
            description: 'Test Location is required',
            expectedResult: 'Error message "Location is required" when field is empty'
          },
          {
            testId: 'claims-field-3',
            description: 'Test Appointment Date is required',
            expectedResult: 'Error message when date is cleared, auto-fills to today"s date by default'
          },
          {
            testId: 'claims-field-4',
            description: 'Test Document Date is required',
            expectedResult: 'Error message when cleared, auto-fills to appointment date or today'
          },
          {
            testId: 'claims-field-5',
            description: 'Test Patient Seen field is required',
            expectedResult: 'Error message "Patient Seen is required" when not selected'
          }
        ]
      },
      {
        title: 'Conditional Field Requirements',
        tests: [
          {
            testId: 'claims-conditional-1',
            description: 'Test Insurance required when billing status is "billed"',
            expectedResult: '"Insurance is required" error appears when billing status = billed and insurance is empty'
          },
          {
            testId: 'claims-conditional-2',
            description: 'Test Insurance NOT required when billing status is "insuranceIssue"',
            expectedResult: 'No insurance error when billing status = insuranceIssue'
          },
          {
            testId: 'claims-conditional-3',
            description: 'Test Insurance NOT required when billing status is "appointmentCancelled"',
            expectedResult: 'No insurance error when billing status = appointmentCancelled'
          },
          {
            testId: 'claims-conditional-4',
            description: 'Test Payment Date required when any CPT has paid > 0',
            expectedResult: '"Payment Date is required" error when CPT has paid amount but no payment date'
          }
        ]
      },
      {
        title: 'Auto-Fill and Business Rules',
        tests: [
          {
            testId: 'claims-autofill-1',
            description: 'Test Provider auto-selection with single provider',
            expectedResult: 'Appointment Provider automatically populates when only one provider available for account'
          },
          {
            testId: 'claims-autofill-2',
            description: 'Test Patient Seen auto-sets to NO when billing status = "appointmentCancelled"',
            expectedResult: 'Patient Seen automatically changes to "No" and becomes disabled'
          },
          {
            testId: 'claims-autofill-3',
            description: 'Test Patient Seen auto-sets to YES when billing status = "billed"',
            expectedResult: 'Patient Seen automatically changes to "Yes"'
          },
          {
            testId: 'claims-autofill-4',
            description: 'Test Document Uploaded auto-sets to YES when document date exists',
            expectedResult: 'Document Uploaded automatically becomes "Yes" when document date is filled'
          },
          {
            testId: 'claims-autofill-5',
            description: 'Test Payment Date auto-fills to last day of month for billed claims with payments',
            expectedResult: 'Payment Date automatically sets to last day of current month'
          }
        ]
      },
      {
        title: 'CPT Activity Validation',
        tests: [
          {
            testId: 'claims-cpt-1',
            description: 'Test CPT Code is required',
            expectedResult: '"CPT Code is required" error message for empty CPT code field'
          },
          {
            testId: 'claims-cpt-2',
            description: 'Test Date of Service is required',
            expectedResult: '"Date of Service is required" error for empty DOS field'
          },
          {
            testId: 'claims-cpt-3',
            description: 'Test DOS within ±30 days of appointment date',
            expectedResult: 'Error "Date of Service must be within 30 days of appointment date" for dates outside range'
          },
          {
            testId: 'claims-cpt-4',
            description: 'Test ICD code is required',
            expectedResult: '"ICD code is required" error message for empty ICD field'
          },
          {
            testId: 'claims-cpt-5',
            description: 'Test Paid amount range 0-5000',
            expectedResult: 'Error message for amounts outside 0-5000 range'
          },
          {
            testId: 'claims-cpt-6',
            description: 'Test PR amount range 0-5000',
            expectedResult: 'Error message for PR amounts outside 0-5000 range'
          },
          {
            testId: 'claims-cpt-7',
            description: 'Test PR Type required when PR > 0',
            expectedResult: '"PR Type is required" error when PR amount > 0 but PR Type is empty'
          },
          {
            testId: 'claims-cpt-8',
            description: 'Test Payment Date required when paid > 0',
            expectedResult: '"Payment Date is required" error when paid amount > 0 but payment date is empty'
          }
        ]
      },
      {
        title: 'CPT Auto-Fill Rules',
        tests: [
          {
            testId: 'claims-cpt-auto-1',
            description: 'Test Allowed amount auto-calculation',
            expectedResult: 'Allowed field automatically updates to sum of Paid + PR amounts and is read-only'
          },
          {
            testId: 'claims-cpt-auto-2',
            description: 'Test CPT Status auto-sets to "paid" when paid > 0 and any PR',
            expectedResult: 'CPT Status automatically becomes "paid"'
          },
          {
            testId: 'claims-cpt-auto-3',
            description: 'Test CPT Status auto-sets to "patientResponsibility" when paid = 0 and PR > 0',
            expectedResult: 'CPT Status automatically becomes "patientResponsibility"'
          },
          {
            testId: 'claims-cpt-auto-4',
            description: 'Test Auto-add CPT 96136 and 96137 for psychoEmotional assessments',
            expectedResult: 'Both CPT codes automatically added with modifier 59 for 96136, ICD pre-filled'
          },
          {
            testId: 'claims-cpt-auto-5',
            description: 'Test Adding CPT 96136 auto-adds 96137 if not present',
            expectedResult: 'CPT 96137 row automatically appears when 96136 is manually added'
          },
          {
            testId: 'claims-cpt-auto-6',
            description: 'Test Adding CPT 99358 sets DOS to appointment date + 3 days',
            expectedResult: 'DOS field automatically fills with date 3 days after appointment date'
          }
        ]
      },
      {
        title: 'Claim Status Logic',
        tests: [
          {
            testId: 'claims-status-1',
            description: 'Test Claim Status = "notBillable" when billing status = "insuranceIssue"',
            expectedResult: 'Claim Status automatically becomes "notBillable" and cannot be changed'
          },
          {
            testId: 'claims-status-2',
            description: 'Test Claim Status = "notBillable" when billing status = "appointmentCancelled"',
            expectedResult: 'Claim Status automatically becomes "notBillable" and cannot be changed'
          },
          {
            testId: 'claims-status-3',
            description: 'Test Claim Status = "billed" when billing status = "billed" and no CPT rows',
            expectedResult: 'Claim Status automatically becomes "billed"'
          },
          {
            testId: 'claims-status-4',
            description: 'Test Claim Status = "paid" when all CPT activities are paid',
            expectedResult: 'Claim Status automatically becomes "paid" when all CPT rows have paid amounts'
          },
          {
            testId: 'claims-status-5',
            description: 'Test Claim Status = "partiallyPaid" when some CPT activities are paid',
            expectedResult: 'Claim Status automatically becomes "partiallyPaid"'
          },
          {
            testId: 'claims-status-6',
            description: 'Test Claim Status = "patientResponsibility" when all CPT have PR only',
            expectedResult: 'Claim Status automatically becomes "patientResponsibility"'
          },
          {
            testId: 'claims-status-7',
            description: 'Test Claim Status = "pending" when billing status = "pending"',
            expectedResult: 'Claim Status automatically becomes "pending" and other options are disabled'
          },
          {
            testId: 'claims-status-8',
            description: 'Test Claim Status = "pending" when billing status = "hold"',
            expectedResult: 'Claim Status automatically becomes "pending" and other options are disabled'
          },
          {
            testId: 'claims-status-9',
            description: 'Test Claim Status = "denied" when all CPT statuses are denied',
            expectedResult: 'Claim Status automatically becomes "denied" when all CPT activities have denied status'
          },
          {
            testId: 'claims-status-10',
            description: 'Test Claim Status restrictions based on billing status',
            expectedResult: 'System disables invalid Claim Status options based on current billing status and CPT data'
          }
        ]
      },
      {
        title: 'API Integration Testing',
        tests: [
          {
            testId: 'claims-api-1',
            description: 'Test claim save with valid data',
            expectedResult: 'Success message "Claim saved successfully", page remains on claim edit with updated timestamps'
          },
          {
            testId: 'claims-api-2',
            description: 'Test claim save with validation errors',
            expectedResult: 'Specific error messages displayed for each invalid field, form prevents submission'
          },
          {
            testId: 'claims-api-3',
            description: 'Test lookup data loading for providers/insurance/CPT codes',
            expectedResult: 'Dropdown options populate correctly from API, search functionality works'
          },
          {
            testId: 'claims-api-4',
            description: 'Test ICD codes dynamic search',
            expectedResult: 'ICD codes filter as user types, displays code + description, selection populates field'
          }
        ]
      },
      {
        title: 'Permission Testing',
        tests: [
          {
            testId: 'claims-perm-1',
            description: 'Test CLAIM_GET permission for viewing claim details',
            expectedResult: 'User can access claim edit page and see all claim data'
          },
          {
            testId: 'claims-perm-2',
            description: 'Test CLAIM_PATCH permission for editing claims',
            expectedResult: 'Save button is enabled and user can successfully update claim data'
          },
          {
            testId: 'claims-perm-3',
            description: 'Test access denied without proper permissions',
            expectedResult: '"Access denied" message or redirect to authorized page'
          }
        ]
      },
      {
        title: 'Assessment Auto-Population Testing',
        tests: [
          {
            testId: 'claims-assessment-1',
            description: 'Test PHQ-16 assessment auto-population of CPT codes',
            expectedResult: 'Completed PHQ-16 assessment automatically adds CPT codes 96136 and 96137 with modifier 59, DOS set to appointment date'
          },
          {
            testId: 'claims-assessment-2',
            description: 'Test other psychoEmotional assessment auto-population',
            expectedResult: 'Other completed psychoEmotional assessments trigger appropriate CPT code auto-population'
          }
        ]
      },
      {
        title: 'Report Generation Testing',
        tests: [
          {
            testId: 'claims-report-1',
            description: 'Test claim page list report download',
            expectedResult: 'Download button generates and downloads claim report in appropriate format with all visible data'
          },
          {
            testId: 'claims-report-2',
            description: 'Test claim report data accuracy',
            expectedResult: 'Downloaded report contains accurate claim data matching what"s displayed on screen'
          }
        ]
      }
    ]
  },

  'documentation-system': {
    sectionId: 'documentation-system',
    title: 'Documentation System Testing',
    subsections: [
      {
        title: 'Global Search System Testing',
        tests: [
          {
            testId: 'doc-search-1',
            description: 'Test global search field activation and input',
            expectedResult: 'Search field accepts input, dropdown appears on typing, shows loading state during search'
          },
          {
            testId: 'doc-search-2',
            description: 'Test search results dropdown display and formatting',
            expectedResult: 'Results show breadcrumb paths, highlighted snippets, clean text without HTML markup'
          },
          {
            testId: 'doc-search-3',
            description: 'Test multiple results from same page functionality',
            expectedResult: 'Search shows all relevant matches from single page, groups results when many from same source'
          },
          {
            testId: 'doc-search-4',
            description: 'Test search term highlighting and snippet extraction',
            expectedResult: 'Search terms highlighted in yellow, snippets show context around matches, no markdown artifacts'
          },
          {
            testId: 'doc-search-5',
            description: 'Test search result navigation and auto-scroll',
            expectedResult: 'Clicking result opens correct page, expands menu sections, scrolls to highlighted text automatically'
          },
          {
            testId: 'doc-search-6',
            description: 'Test search term preservation and clear functionality',
            expectedResult: 'Search term stays in field after navigation, X button appears when text present, clears search and highlighting'
          },
          {
            testId: 'doc-search-7',
            description: 'Test persistent highlighting until manual clear',
            expectedResult: 'Text highlighting remains visible until user manually clears search or navigates normally'
          },
          {
            testId: 'doc-search-8',
            description: 'Test search with various query lengths and types',
            expectedResult: 'Handles short queries (2+ chars), long queries, special characters, numbers, shows appropriate results'
          },
          {
            testId: 'doc-search-9',
            description: 'Test search performance with large result sets',
            expectedResult: 'Search remains responsive with many results, scrollable dropdown, up to 20 results displayed'
          },
          {
            testId: 'doc-search-10',
            description: 'Test search clearing behaviors (manual vs automatic)',
            expectedResult: 'Manual clear via X button, automatic clear when typing empty field, highlight removal on regular navigation'
          }
        ]
      },
      {
        title: 'Navigation and Interface Testing',
        tests: [
          {
            testId: 'doc-nav-1',
            description: 'Test left sidebar navigation menu functionality',
            expectedResult: 'All menu items are clickable, active state highlights correctly, disabled items show as grayed out'
          },
          {
            testId: 'doc-nav-2',
            description: 'Test automatic scroll to top when switching menu items',
            expectedResult: 'Page content scrolls to top instantly when selecting different documentation sections'
          },
          {
            testId: 'doc-nav-3',
            description: 'Test responsive menu behavior',
            expectedResult: 'Menu adapts to different screen sizes, compact layout maintains functionality'
          },
          {
            testId: 'doc-nav-4',
            description: 'Test menu expansion and collapse functionality',
            expectedResult: 'Menu sections expand/collapse correctly, maintain state during navigation, show proper indicators'
          }
        ]
      },
      {
        title: 'Content Display and Formatting Testing',
        tests: [
          {
            testId: 'doc-content-1',
            description: 'Test markdown rendering in documentation sections',
            expectedResult: 'Markdown content displays correctly with proper headers, lists, tables, and formatting'
          },
          {
            testId: 'doc-content-2',
            description: 'Test table formatting and responsiveness',
            expectedResult: 'Documentation tables display properly, are readable on all screen sizes, maintain proper alignment'
          },
          {
            testId: 'doc-content-3',
            description: 'Test code block and syntax highlighting',
            expectedResult: 'Code examples display with proper syntax highlighting, copy functionality works if available'
          },
          {
            testId: 'doc-content-4',
            description: 'Test link functionality in documentation',
            expectedResult: 'Internal and external links work correctly, open in appropriate targets, show hover states'
          },
          {
            testId: 'doc-content-5',
            description: 'Test section collapse/expand functionality',
            expectedResult: 'Collapsible sections toggle correctly, maintain state, show appropriate indicators'
          }
        ]
      },
      {
        title: 'Patient Documentation Testing',
        tests: [
          {
            testId: 'doc-patient-1',
            description: 'Test Patient List documentation completeness',
            expectedResult: 'All fields, permissions, API endpoints, and UI functionality are documented with examples'
          },
          {
            testId: 'doc-patient-2',
            description: 'Test Patient Edit/Create documentation accuracy',
            expectedResult: 'Field specifications match actual form behavior, validation rules are correct'
          },
          {
            testId: 'doc-patient-3',
            description: 'Test patient-related permission documentation',
            expectedResult: 'All patient permissions are documented with correct names and descriptions'
          },
          {
            testId: 'doc-patient-4',
            description: 'Test patient API documentation examples',
            expectedResult: 'API examples show correct request/response formats and status codes'
          }
        ]
      },
      {
        title: 'Claims Documentation Testing',
        tests: [
          {
            testId: 'doc-claims-1',
            description: 'Test Claims List documentation completeness',
            expectedResult: 'All fields, filters, permissions, and export functionality are documented correctly'
          },
          {
            testId: 'doc-claims-2',
            description: 'Test Claims Edit documentation accuracy',
            expectedResult: 'Complex CPT logic, validation rules, and auto-calculations are properly documented'
          },
          {
            testId: 'doc-claims-3',
            description: 'Test claims API documentation with examples',
            expectedResult: 'API endpoints show correct request/response formats for all claim operations'
          },
          {
            testId: 'doc-claims-4',
            description: 'Test claims permission documentation',
            expectedResult: 'All claim-related permissions are documented with proper access controls'
          },
          {
            testId: 'doc-claims-5',
            description: 'Test auto-population rules documentation',
            expectedResult: 'CPT auto-addition from assessments and billing status logic are clearly explained'
          }
        ]
      },
      {
        title: 'Dashboard Documentation Testing',
        tests: [
          {
            testId: 'doc-dashboard-1',
            description: 'Test Dashboard documentation for all widgets and components',
            expectedResult: 'All dashboard widgets, charts, and interactive elements are documented with functionality'
          },
          {
            testId: 'doc-dashboard-2',
            description: 'Test dashboard permission-based content documentation',
            expectedResult: 'Documentation explains which content appears based on user permissions'
          },
          {
            testId: 'doc-dashboard-3',
            description: 'Test dashboard API integration documentation',
            expectedResult: 'All dashboard data sources and API endpoints are documented with examples'
          },
          {
            testId: 'doc-dashboard-4',
            description: 'Test dashboard filter and date range documentation',
            expectedResult: 'Filter functionality and date range controls are properly explained'
          },
          {
            testId: 'doc-dashboard-5',
            description: 'Test dashboard responsive behavior documentation',
            expectedResult: 'Mobile and tablet adaptations of dashboard layout are documented'
          },
          {
            testId: 'doc-dashboard-6',
            description: 'Test dashboard export and reporting documentation',
            expectedResult: 'Dashboard export functionality and report generation are documented'
          }
        ]
      },
      {
        title: 'API Documentation Testing',
        tests: [
          {
            testId: 'doc-api-1',
            description: 'Test API endpoint documentation completeness',
            expectedResult: 'All endpoints documented with HTTP methods, URLs, request/response examples'
          },
          {
            testId: 'doc-api-2',
            description: 'Test API error handling documentation',
            expectedResult: 'All possible error responses documented with status codes and error formats'
          },
          {
            testId: 'doc-api-3',
            description: 'Test API authentication documentation',
            expectedResult: 'Authentication requirements and permission checks are clearly documented'
          },
          {
            testId: 'doc-api-4',
            description: 'Test API request/response schema documentation',
            expectedResult: 'JSON schemas are accurate and match actual API behavior'
          }
        ]
      },
      {
        title: 'Permission Documentation Testing',
        tests: [
          {
            testId: 'doc-perm-1',
            description: 'Test permission naming consistency across documentation',
            expectedResult: 'All permission names match exactly between documentation and actual system'
          },
          {
            testId: 'doc-perm-2',
            description: 'Test conditional functionality documentation',
            expectedResult: 'UI elements that appear/disappear based on permissions are documented correctly'
          },
          {
            testId: 'doc-perm-3',
            description: 'Test Distribution Sponsor permission documentation',
            expectedResult: 'DS-specific permissions and multi-account functionality are clearly explained'
          },
          {
            testId: 'doc-perm-4',
            description: 'Test access denied scenarios documentation',
            expectedResult: 'Error handling for insufficient permissions is documented with examples'
          }
        ]
      },
      {
        title: 'Validation Rules Documentation Testing',
        tests: [
          {
            testId: 'doc-validation-1',
            description: 'Test field validation rules accuracy',
            expectedResult: 'All documented validation rules match actual form behavior and error messages'
          },
          {
            testId: 'doc-validation-2',
            description: 'Test required field documentation',
            expectedResult: 'Required/optional status is correctly documented for all form fields'
          },
          {
            testId: 'doc-validation-3',
            description: 'Test complex validation logic documentation',
            expectedResult: 'Multi-field validation rules and conditional requirements are clearly explained'
          },
          {
            testId: 'doc-validation-4',
            description: 'Test error message documentation',
            expectedResult: 'All validation error messages are documented and match system messages'
          },
          {
            testId: 'doc-validation-5',
            description: 'Test business rule validation documentation',
            expectedResult: 'Business logic constraints are documented with clear explanations and examples'
          }
        ]
      },
      {
        title: 'Search User Experience Testing',
        tests: [
          {
            testId: 'doc-search-ux-1',
            description: 'Test search discoverability and initial user interaction',
            expectedResult: 'Search field is prominent, placeholder text is helpful, visual cues guide user interaction'
          },
          {
            testId: 'doc-search-ux-2',
            description: 'Test search feedback and loading states',
            expectedResult: 'Loading spinner appears during search, progress indicators work, no flickering or jumping'
          },
          {
            testId: 'doc-search-ux-3',
            description: 'Test search result relevance and ranking',
            expectedResult: 'Most relevant results appear first, title matches ranked higher than content matches'
          },
          {
            testId: 'doc-search-ux-4',
            description: 'Test search result click experience and navigation flow',
            expectedResult: 'Smooth transition to selected page, context is preserved, user can easily return to search'
          },
          {
            testId: 'doc-search-ux-5',
            description: 'Test search highlighting effectiveness and visibility',
            expectedResult: 'Highlighted text is easily visible, contrasts well, draws attention appropriately'
          },
          {
            testId: 'doc-search-ux-6',
            description: 'Test search error handling and edge cases',
            expectedResult: 'Graceful handling of no results, special characters, very long queries, network issues'
          }
        ]
      },
      {
        title: 'Documentation Quality and Completeness Testing',
        tests: [
          {
            testId: 'doc-quality-1',
            description: 'Test documentation completeness for all implemented features',
            expectedResult: 'Every implemented feature has corresponding documentation with field specs and APIs'
          },
          {
            testId: 'doc-quality-2',
            description: 'Test documentation accuracy against actual application',
            expectedResult: 'All documented features match exactly what exists in the application'
          },
          {
            testId: 'doc-quality-3',
            description: 'Test documentation formatting and readability',
            expectedResult: 'Documentation uses consistent formatting, clear language, and helpful examples'
          },
          {
            testId: 'doc-quality-4',
            description: 'Test documentation coverage for edge cases',
            expectedResult: 'Special cases, error conditions, and unusual scenarios are documented'
          },
          {
            testId: 'doc-quality-5',
            description: 'Test search integration with documentation system',
            expectedResult: 'Search finds all documented content, indexes are complete, search works across all sections'
          }
        ]
      }
    ]
  }
};

// Function to count total number of tests in a section
export const getTestCount = (sectionData) => {
  return sectionData.subsections.reduce((total, subsection) => {
    return total + subsection.tests.length;
  }, 0);
};

// Function to get all tests from a section (flat list)
export const getAllTests = (sectionData) => {
  const allTests = [];
  sectionData.subsections.forEach(subsection => {
    subsection.tests.forEach(test => {
      allTests.push({
        ...test,
        sectionId: sectionData.sectionId,
        subsectionTitle: subsection.title
      });
    });
  });
  return allTests;
};